import { useMemo } from 'react';

import type { AiCoach_AdditionalContextInput as AiCoachAdditionalContextInput } from '__generated__/graphql-types';

import { useLocation } from 'js/lib/useRouter';
import { isAuthenticatedUser } from 'js/lib/user';

type SurfaceInfo = {
  surface_type: string;
  topical_anchor?: string;
};

const slugToDisplayName = (slug: string): string => {
  return slug.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

export const SURFACE_CONTEXT_KEY = 'additionalContext.surface_context';

export const getSurfaceInfoFromPath = (pathname: string): SurfaceInfo | null => {
  // Career Academy & RDP: /career-academy, /career-academy/roles/:roleSlug
  if (pathname.startsWith('/career-academy')) {
    const roleMatch = pathname.match(/^\/career-academy(?:\/roles\/([^/]+))?/);
    const roleSlug = roleMatch?.[1];
    return { surface_type: 'career_academy', ...(roleSlug ? { topical_anchor: slugToDisplayName(roleSlug) } : {}) };
  }

  // LOHP/LIHP: /
  if (pathname === '/') {
    const isAuthenticated = isAuthenticatedUser();
    return { surface_type: isAuthenticated ? 'lihp' : 'lohp' };
  }

  // LOHP: /frontpage
  if (pathname === '/frontpage') {
    return { surface_type: 'lohp' };
  }

  // Premium Hub pages: /certificates, /certificates/learn, /certificates/:categorySlug, /mastertrack, /professional-certificates
  if (pathname.startsWith('/certificates')) {
    const certCategoryMatch = pathname.match(/^\/certificates\/([^/]+)/);
    if (!certCategoryMatch) {
      return { surface_type: 'category', topical_anchor: 'Certificates Hub' };
    }

    const matchCategory = certCategoryMatch?.[1];
    if (matchCategory === 'learn') {
      return { surface_type: 'category', topical_anchor: 'University Certificates Hub' };
    }

    return { surface_type: 'category', ...(matchCategory ? { topical_anchor: slugToDisplayName(matchCategory) } : {}) };
  }
  if (pathname.startsWith('/mastertrack')) {
    return { surface_type: 'category', topical_anchor: 'Mastertrack Certificates Hub' };
  }
  if (pathname.startsWith('/professional-certificates')) {
    return { surface_type: 'category', topical_anchor: 'Professional Certificates Hub' };
  }

  // Explore pages: /explore/:slug, /google-career-certificates, /meta-professional-certificates
  if (pathname.startsWith('/explore')) {
    const exploreMatch = pathname.match(/^\/explore\/([^/]+)/);
    if (exploreMatch) {
      const exploreSlug = exploreMatch?.[1];
      return { surface_type: 'explore', ...(exploreSlug ? { topical_anchor: slugToDisplayName(exploreSlug) } : {}) };
    }
  }
  if (pathname === '/google-career-certificates') {
    return { surface_type: 'explore', topical_anchor: 'Google Career Certificates' };
  }
  if (pathname === '/meta-professional-certificates') {
    return { surface_type: 'explore', topical_anchor: 'Meta Professional Certificates' };
  }

  // Collections pages: /collections/:slug (e.g. collections/remote-work-strategies)
  const collectionsMatch = pathname.match(/^\/collections\/([^/]+)/);
  if (collectionsMatch) {
    const collectionsSlug = collectionsMatch?.[1];
    return {
      surface_type: 'collection',
      ...(collectionsSlug ? { topical_anchor: slugToDisplayName(collectionsSlug) } : {}),
    };
  }

  // Coursera Plus Landing Page: /courseraplus
  if (pathname.startsWith('/courseraplus')) {
    return { surface_type: 'coursera_plus', topical_anchor: 'Coursera Plus' };
  }

  // Partners pages: /partners, /partners/:shortName, /about/partners/preview/:shortName
  if (pathname.startsWith('/partners') || pathname.startsWith('/about/partners/preview')) {
    const partnerMatch =
      pathname.match(/^\/partners(?:\/([^/]+))?/) ?? pathname.match(/^\/about\/partners\/preview\/([^/]+)/);
    return { surface_type: 'partner', ...(partnerMatch?.[1] ? { topical_anchor: partnerMatch?.[1] } : {}) };
  }

  // Browse pages: /browse, /browse/:domainSlug, /browse/:domainSlug/:subdomainSlug
  const browseMatch = pathname.match(/^\/browse(?:\/([^/?#]+)(?:\/([^/?#]+))?)?(?=[?#]|$)/);
  if (browseMatch) {
    const domain = browseMatch[1];
    const subdomain = browseMatch[2];

    if (domain && subdomain) {
      return { surface_type: 'browse', topical_anchor: `${slugToDisplayName(domain)}/${slugToDisplayName(subdomain)}` };
    }

    if (domain) {
      return { surface_type: 'browse', topical_anchor: slugToDisplayName(domain) };
    }

    return { surface_type: 'browse' };
  }

  return null;
};

const useNavAiSparkleSurfaceInfo = (): AiCoachAdditionalContextInput[] => {
  const { pathname } = useLocation();

  return useMemo(() => {
    const info = getSurfaceInfoFromPath(pathname);
    if (!info) return [];

    const entries: AiCoachAdditionalContextInput[] = [
      { contextKey: SURFACE_CONTEXT_KEY, contextValue: JSON.stringify(info) },
    ];

    return entries;
  }, [pathname]);
};

export default useNavAiSparkleSurfaceInfo;
